import { signalStore, withState, withMethods, patchState, withComputed } from '@ngrx/signals';
import { computed, inject } from '@angular/core';
import { ViewSubmissionService } from './view-submission.service';
import {
  SubmissionSummaryViewModel,
  QuoteBreakdownViewModel,
  MGAPremiumBreakDownDetailViewModel,
  DocumentGridRowViewModel,
  SubmissionStatusEnum,
  SubmissionReviewViewModel,
  PolicyChangeGridViewModel,
  LossRunsRequestGridViewModel,
  LossRunsRequestREQ,
  LookupViewModel,
} from '@program-suite/data-common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackbarComponent, ToastType } from '@program-suite/ui';
// Temporary type definition until API models are regenerated
interface PriorWorkViewModel {
  removePriorWork?: boolean;
  priorWorksEffectiveDate?: string | null;
  issuedEffectiveDate?: string | Date | null;
}

interface ViewSubmission {
  summary?: SubmissionSummaryViewModel | null;
  quoteBreakdown?: QuoteBreakdownViewModel | null;
  premiumBreakdown?: MGAPremiumBreakDownDetailViewModel | null;
  documents?: DocumentGridRowViewModel[] | null;
  policyDocuments?: DocumentGridRowViewModel[] | null;
  endorsements?: PolicyChangeGridViewModel | null;
  lossRuns?: LossRunsRequestGridViewModel | null;
  submissionReview?: SubmissionReviewViewModel | null;
  isLoading?: boolean;
}

const initialState: ViewSubmission = {
  summary: null,
  quoteBreakdown: null,
  premiumBreakdown: null,
  documents: null,
  policyDocuments: null,
  endorsements: null,
  lossRuns: null,
  isLoading: false,
  submissionReview: null,
};

export const ViewSubmissionStore = signalStore(
  { providedIn: 'root' },
  withState<ViewSubmission>(initialState),
  withComputed((state) => ({
    debug: computed(() => ({
      summary: state.summary,
      quoteBreakdown: state.quoteBreakdown,
      premiumBreakdown: state.premiumBreakdown,
      documents: state.documents,
      policyDocuments: state.policyDocuments,
      endorsements: state.endorsements,
      isLoading: state.isLoading,
    })),
    applicationDocuments: computed(() => {
      const documents = state.documents;
      return (documents?.() ?? []).filter((doc) => doc.documentType === 'Application');
    }),
    lossRunDocuments: computed(() => {
      const documents = state.documents;
      return (documents?.() ?? []).filter((doc) => doc.documentType === 'LossRun');
    }),
  })),
  withMethods((store, viewSubmissionService = inject(ViewSubmissionService), snackBar = inject(MatSnackBar)) => ({
    setSubmissionSummary(submissionSummary: SubmissionSummaryViewModel) {
      patchState(store, {
        summary: submissionSummary,
      });
    },
    async getSubmissionSummary(guid: string, isPolicyPage: boolean) {
      var submissionSummary!: SubmissionSummaryViewModel;
      if (isPolicyPage) {
        submissionSummary = await viewSubmissionService.getPolicySummary(guid);
      } else {
        submissionSummary = await viewSubmissionService.getSubmissionSummary(guid);
      }
      this.setSubmissionSummary(submissionSummary);
    },
    setSubmissionReview(submissionReview: SubmissionReviewViewModel | null) {
      patchState(store, {
        submissionReview: submissionReview,
      });
    },
    async getSubmissionReview(submissionGuid: string) {
      const submissionReview = await viewSubmissionService.getSubmissionReview(submissionGuid);
      this.setSubmissionReview(submissionReview);
    },
    setQuoteBreakdown(quoteBreakdown: QuoteBreakdownViewModel) {
      patchState(store, {
        quoteBreakdown: quoteBreakdown,
      });
    },
    async getSubmissionPricingTotals(guid: string, isPolicyPage: boolean) {
      var quoteBreakdown!: QuoteBreakdownViewModel;
      if (isPolicyPage) {
        quoteBreakdown = await viewSubmissionService.getPolicyPricingTotals(guid);
      } else {
        quoteBreakdown = await viewSubmissionService.getSubmissionPricingTotals(guid);
      }
      this.setQuoteBreakdown(quoteBreakdown);
    },
    setPremiumBreakdown(premiumBreakdown: MGAPremiumBreakDownDetailViewModel) {
      patchState(store, {
        premiumBreakdown: premiumBreakdown,
      });
    },
    async getSubmissionPremiumBreakdown(guid: string, isPolicyPage: boolean) {
      var premiumBreakdown!: MGAPremiumBreakDownDetailViewModel;
      if (isPolicyPage) {
        premiumBreakdown = await viewSubmissionService.getPolicyPremiumBreakdown(guid);
      } else {
        premiumBreakdown = await viewSubmissionService.getSubmissionPremiumBreakdown(guid);
      }
      this.setPremiumBreakdown(premiumBreakdown);
    },
    setDocuments(documents: DocumentGridRowViewModel[]) {
      patchState(store, {
        documents: documents,
      });
    },
    async getSubmissionDocuments(submissionGuid: string, isPolicyPage: boolean) {
      var documents!: DocumentGridRowViewModel[];
      if (isPolicyPage) {
        documents = await viewSubmissionService.getPolicySubmissionDocuments(submissionGuid);
      } else {
        documents = await viewSubmissionService.getSubmissionDocuments(submissionGuid);
      }

      this.setDocuments(documents);
    },
    setPolicyDocuments(policyDocuments: DocumentGridRowViewModel[]) {
      patchState(store, {
        policyDocuments: policyDocuments,
      });
    },
    async getPolicyDocuments(policyGuid: string) {
      const policyDocuments = await viewSubmissionService.getPolicyDocuments(policyGuid);
      this.setPolicyDocuments(policyDocuments);
    },
    setEndorsements(endorsements: PolicyChangeGridViewModel) {
      patchState(store, {
        endorsements: endorsements,
      });
    },
    async getEndorsements(model: any) {
      const endorsements = await viewSubmissionService.getEndorsements(model);
      this.setEndorsements(endorsements);
    },
   async downloadLossRun(lossRunsGuid: string, throwToast?: boolean): Promise<void> {
      try {
        if (throwToast) {
          snackBar.openFromComponent(SnackbarComponent, {
            duration: 10000,
            data: {
              toastType: ToastType.Success,
              title: 'Download Processing',
              message: 'Your file will automatically download shortly. This may take a few seconds.',
            },
          });
        }
        await viewSubmissionService.downloadLossRun(lossRunsGuid);
      } catch (error) {
        console.error('Error downloading document:', error);
        throw new Error('Failed to download the document, please try again');
      }
    },
    async getLossRuns(model: any): Promise<LossRunsRequestGridViewModel> {
      const lossRuns = await viewSubmissionService.getLossRuns(model);
      patchState(store, {
        lossRuns: lossRuns,
      });
      return lossRuns;
    },
    setLossRuns(lossRuns: LossRunsRequestGridViewModel) {
      patchState(store, {
        lossRuns: lossRuns,
      });
    },

    async submitLossRunRequest(model: LossRunsRequestREQ): Promise<void> {
      try {
        const response = await viewSubmissionService.submitLossRunRequest(model);
        if (response.statusId ==2) {
          await this.getLossRuns
        snackBar.openFromComponent(SnackbarComponent, {
          duration: 5000,
          data: {
            toastType: ToastType.Success,
            title: 'Success',
            message: 'Your loss runs request has been issued automatically.',
          },
        });
      }
       else {
        snackBar.openFromComponent(SnackbarComponent, {
          duration: 5000,
          data: {
            toastType: ToastType.Warning,
            title: 'Warning',
            message: 'Your loss runs request requires additional review. An underwriter will update your request shortly. ',
          },
        });
      }
     } catch (error) {
        console.error('Error submitting loss run request:', error);
        throw new Error('Failed to submit the loss run request, please try again');
      }
    },
    async deleteSubmissionDocument(document: DocumentGridRowViewModel): Promise<void> {
      await viewSubmissionService.deleteSubmissionDocument(document);
    },
    async downloadDocument(documentGuid: string, throwToast?: boolean, policyGuid?: string): Promise<void> {
      try {
        if (throwToast) {
          snackBar.openFromComponent(SnackbarComponent, {
            duration: 10000,
            data: {
              toastType: ToastType.Success,
              title: 'Download Processing',
              message: 'Your file will automatically download shortly. This may take a few seconds.',
            },
          });
        }
        await viewSubmissionService.downloadDocument(documentGuid, policyGuid);
      } catch (error) {
        console.error('Error downloading document:', error);
        throw new Error('Failed to download the document, please try again');
      }
    },
    async uploadSubmissionDocument(model: FormData, isPolicyPage: boolean): Promise<void> {
      try {
        if (isPolicyPage) {
          await viewSubmissionService.uploadPolicySubmissionDocument(model);
        } else {
          await viewSubmissionService.uploadSubmissionDocument(model);
        }
        this.getSubmissionDocuments(model.get('guid') as string, isPolicyPage);
      } catch (error) {
        console.error('Error uploading submission document:', error);
        throw new Error('Failed to upload the document, please try again');
      }
    },
    async updateSubmissionStatus(submissionGuid: string, status: SubmissionStatusEnum): Promise<void> {
      await viewSubmissionService.updateSubmissionStatus(submissionGuid, status);
    },
    async updateSubmissionProducer(submissionGuid: string, data: any): Promise<void> {
      await viewSubmissionService.updateSubmissionProducer(submissionGuid, data);
    },
    async submitBindRequest(model: any): Promise<void> {
      try {
        await viewSubmissionService.submitBindRequest(model);
        this.getSubmissionSummary(model.submissionGuid, model.isPolicyPage);
      } catch (error) {
        console.error('Error updating submission status and proposed effective date:', error);
        throw new Error('Failed to update the submission status and proposed effective date, please try again');
      }
    },
    async issuePolicy(submissionGuid: string, pwvm: PriorWorkViewModel): Promise<void> {
      await viewSubmissionService.issuePolicy(submissionGuid, pwvm);
    },
    setLoading(isLoading: boolean) {
      patchState(store, {
        isLoading: isLoading,
      });
    },
    reset() {
      patchState(store, initialState);
    },
  })),
);
